import React, {
  Fragment,
  Reducer,
  useCallback,
  useEffect,
  useReducer,
} from 'react'
import { FaTrashAlt } from 'react-icons/fa'
import { v4 as uuid } from 'uuid'
import { Button } from '@apex/component-library'
import CashTypeSelect from './CashTypeSelect'
import { Grid, GridItem, GridProps, Icon, Text } from '@chakra-ui/react'
import { CurrencyEnum } from '../../../utils/numberToCurrency'
import { isEmpty } from 'lodash'
import {
  TransferInitiationCashType,
  TransferInitiationCashWebDto,
} from '../../../api/types'
import AmountInput from './AmountInput'

type CashAction = {
  type: 'ADD' | 'REMOVE' | 'UPDATE'
  rowId: string
  value?: TransferInitiationCashType | number
  field?: keyof TransferInitiationCashWebDto
}

const MAX_CASH_ROWS = 2

type CashEntry = Omit<TransferInitiationCashWebDto, 'amount'> & {
  amount?: number
}

const cashReducer: Reducer<CashEntry[], CashAction> = (
  state: CashEntry[],
  { type: actionType, rowId, field, value }: CashAction
) => {
  switch (actionType) {
    case 'ADD':
      if (state.length >= MAX_CASH_ROWS) {
        return state
      } else {
        let type
        //There can be only 2 rows, based on 2 cash types.
        //First defaults to Credit, second defaults to the unused type.
        //The selects are disabled once a 2nd row is added.
        if (isEmpty(state)) {
          type = TransferInitiationCashType.Credit
        } else {
          type =
            state[0].type === TransferInitiationCashType.Credit
              ? TransferInitiationCashType.Debit
              : TransferInitiationCashType.Credit
        }
        return state.concat({
          rowId,
          type,
          isoCurrencyCode: CurrencyEnum.usd,
        })
      }
    case 'REMOVE':
      return state.filter(cashEntry => cashEntry.rowId !== rowId)
    case 'UPDATE':
      return state.map(cashEntry =>
        cashEntry.rowId === rowId
          ? { ...cashEntry, [field!]: value }
          : cashEntry
      )
    default:
      return state
  }
}

interface CashAssetsFormProps extends GridProps {
  formikErrors?: string
  setFieldValue: (field: string, value: any) => void
  cash?: TransferInitiationCashWebDto[]
}

const CashAssetsForm = ({
  formikErrors,
  setFieldValue,
  cash,
  ...baseGridProps
}: CashAssetsFormProps) => {
  const setFieldValueMemo = useCallback(setFieldValue, [])
  const [cashEntries, dispatch] = useReducer(cashReducer, cash || [])
  useEffect(() => {
    setFieldValueMemo('cash', cashEntries)
  }, [cashEntries, setFieldValueMemo])

  const errorRows = formikErrors?.split(';')
  const isDisabled = cash && cash.length >= MAX_CASH_ROWS
  const cashTransferSpan = 4
  const typeSpan = 2
  const amountSpan = 4
  const currencySpan = 2
  const trashSpan = 9 // Should bring totalColumnSpan up to 21 for form consistency
  const totalColumnsSpan =
    cashTransferSpan + typeSpan + amountSpan + currencySpan + trashSpan

  return (
    <Grid
      paddingLeft="1rem"
      paddingBottom="1rem"
      borderBottom="1px solid gray"
      columnGap={4}
      rowGap={4}
      templateRows={`repeat(${cash?.length}, fit-content 1fr)`}
      templateColumns={`repeat(${totalColumnsSpan}, 1fr)`}
      {...baseGridProps}
    >
      <GridItem colSpan={cashTransferSpan} rowSpan={cash?.length! + 1}>
        <Text fontWeight="semibold">Cash Transfer</Text>
        <Button
          data-testid="add-cash-row-button"
          marginTop="1rem"
          variant="link"
          onClick={() => dispatch({ type: 'ADD', rowId: uuid() })}
          isDisabled={isDisabled}
        >
          + Add Cash
        </Button>
      </GridItem>
      <GridItem colSpan={typeSpan} hidden={!cash?.length}>
        <Text fontWeight="semibold">Type*</Text>
      </GridItem>
      <GridItem colSpan={amountSpan} justifySelf="end" hidden={!cash?.length}>
        <Text fontWeight="semibold">Amount*</Text>
      </GridItem>
      <GridItem colSpan={currencySpan + trashSpan} hidden={!cash?.length}>
        <Text fontWeight="semibold">Currency*</Text>
      </GridItem>
      {cash &&
        cash.map(({ rowId, type, amount }) => (
          <Fragment key={rowId}>
            <GridItem colSpan={typeSpan}>
              <CashTypeSelect
                isDisabled={isDisabled}
                value={type}
                handleChange={newTypeValue =>
                  dispatch({
                    type: 'UPDATE',
                    rowId: rowId!,
                    field: 'type',
                    value: newTypeValue,
                  })
                }
                width="100%"
              />
            </GridItem>
            <GridItem colSpan={amountSpan}>
              <AmountInput
                rowId={rowId!}
                errorRows={errorRows}
                value={amount}
                handleChange={newAmountValue =>
                  dispatch({
                    type: 'UPDATE',
                    rowId: rowId!,
                    field: 'amount',
                    value: newAmountValue,
                  })
                }
                width="100%"
              />
            </GridItem>
            <GridItem colSpan={currencySpan} paddingTop="0.5rem">
              <Text>{CurrencyEnum.usd}</Text>
            </GridItem>
            <GridItem colSpan={trashSpan} justifySelf="end">
              <Button
                data-testid="delete-cash-row-button"
                onClick={() => dispatch({ type: 'REMOVE', rowId: rowId! })}
                variant="link"
              >
                <Icon as={FaTrashAlt} color="gray.400" h={6} w={6} />
              </Button>
            </GridItem>
          </Fragment>
        ))}
    </Grid>
  )
}

export default CashAssetsForm
